import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Space,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  fetchPromoCodes,
  savePromoCode,
  deletePromoCode,
  fetchAllUsers,
} from '../../services/firestoreService';
import {
  applyPromoCodeToUser,
  formatPromoDuration,
  normalizePromoCode,
  validatePromoCode,
} from '../../services/promoCodeService';
import { ADMIN_PROMO_COLLECTION } from '../../firebase/collections';
import moment from 'moment';

const { Option } = Select;

const DURATION_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
  { value: 'lifetime', label: 'Lifetime' },
];

const PromoCodeManagement = () => {
  const { admin, logActivity } = useAdminAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isApplyModalVisible, setIsApplyModalVisible] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form] = Form.useForm();
  const [applyForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const durationUnit = Form.useWatch('durationUnit', form);

  useEffect(() => {
    if (!admin) return;
    loadPromoCodes();
    loadUsers();
    logActivity('view_promo_codes', 'Viewed promo code management page');
  }, [admin]);

  const loadPromoCodes = async () => {
    setLoading(true);
    try {
      const codes = await fetchPromoCodes();
      setPromoCodes(codes);
    } catch (error) {
      message.error(error?.message || 'Failed to load promo codes from Firebase');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const allUsers = await fetchAllUsers();
      setUsers(allUsers.filter((u) => u.status !== 'deleted'));
    } catch {
      message.error('Failed to load users');
    }
  };

  const handleCreateCode = () => {
    setEditingCode(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      durationUnit: 'days',
      durationValue: 30,
    });
    setIsModalVisible(true);
  };

  const handleEditCode = (code) => {
    setEditingCode(code);
    form.setFieldsValue({
      code: code.code,
      description: code.description,
      durationValue: code.duration?.value ?? code.durationValue ?? 30,
      durationUnit: code.duration?.unit ?? code.durationUnit ?? 'days',
      maxUses: code.maxUses ?? undefined,
      status: code.status || 'active',
      validFrom: code.validFrom ? moment(code.validFrom) : null,
      expiryDate: code.expiryDate ? moment(code.expiryDate) : null,
    });
    setIsModalVisible(true);
  };

  const handleSaveCode = () => {
    form.validateFields().then(async (values) => {
      const normalized = normalizePromoCode(values.code);
      const formatError = validatePromoCode(normalized);
      if (formatError) {
        message.error(formatError);
        return;
      }

      const codeData = {
        id: editingCode ? editingCode.id : undefined,
        code: normalized,
        description: values.description || '',
        durationValue: values.durationUnit === 'lifetime' ? 1 : values.durationValue,
        durationUnit: values.durationUnit,
        lifetime: values.durationUnit === 'lifetime',
        maxUses: values.maxUses ?? null,
        validFrom: values.validFrom ? values.validFrom.toDate() : null,
        expiryDate: values.expiryDate ? values.expiryDate.toDate() : null,
        status: values.status || 'active',
      };

      setSaving(true);
      try {
        await savePromoCode(codeData);
        if (editingCode) {
          message.success('Promo code updated successfully');
          logActivity('update_promo_code', `Updated promo code: ${codeData.code}`);
        } else {
          message.success('Promo code created successfully');
          logActivity('create_promo_code', `Created promo code: ${codeData.code}`);
        }
        await loadPromoCodes();
        setIsModalVisible(false);
      } catch (error) {
        message.error(error.message || 'Failed to save promo code');
      } finally {
        setSaving(false);
      }
    });
  };

  const handleDeleteCode = async (code) => {
    try {
      await deletePromoCode(code.id);
      await loadPromoCodes();
      message.success(`Promo code ${code.code} deleted`);
      logActivity('delete_promo_code', `Deleted promo code: ${code.code}`);
    } catch {
      message.error('Failed to delete promo code');
    }
  };

  const handleApplyPromoCode = () => {
    setIsApplyModalVisible(true);
    applyForm.resetFields();
  };

  const handleApplyToUser = () => {
    applyForm.validateFields().then(async (values) => {
      const { promoCodeId, username } = values;

      const code = promoCodes.find((c) => c.id === promoCodeId);
      if (!code) {
        message.error('Invalid promo code');
        return;
      }

      const targetUser = users.find((u) => u.username === username);
      if (!targetUser) {
        message.error('User not found');
        return;
      }

      setApplying(true);
      try {
        const result = await applyPromoCodeToUser({
          promoDocumentId: code.id,
          uid: targetUser.uid,
          collectionName: code.collectionName || ADMIN_PROMO_COLLECTION,
        });

        await loadPromoCodes();
        await loadUsers();
        setIsApplyModalVisible(false);

        const expiryText = result.isLifetime
          ? 'lifetime Premium'
          : `Premium until ${new Date(result.expiresAt).toLocaleDateString()}`;

        message.success(
          `Promo ${result.promoCode} applied to ${username} (${expiryText})`
        );
        logActivity(
          'apply_promo_code',
          `Applied promo code ${result.promoCode} to user: ${username}`
        );
      } catch (error) {
        message.error(error.message || 'Failed to apply promo code');
      } finally {
        setApplying(false);
      }
    });
  };

  const getStats = () => {
    const activeCodes = promoCodes.filter((c) => c.status === 'active').length;
    const totalUses = promoCodes.reduce((sum, c) => sum + (c.usedCount || 0), 0);
    const expiredCodes = promoCodes.filter(
      (c) => c.expiryDate && new Date(c.expiryDate) < new Date()
    ).length;

    return { activeCodes, totalUses, expiredCodes };
  };

  const stats = getStats();

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <Tag color="purple" className="font-mono text-base">
          {code}
        </Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (value) => value || '—',
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => formatPromoDuration(record.duration || record),
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (_, record) => {
        const maxUses = record.maxUses;
        const usedCount = record.usedCount || 0;
        const isFull =
          maxUses != null && Number(maxUses) > 0 && usedCount >= Number(maxUses);

        return (
          <span>
            {usedCount}
            {maxUses != null && Number(maxUses) > 0 ? ` / ${maxUses}` : ' / ∞'}
            {isFull && (
              <Tag color="red" className="ml-2">
                Full
              </Tag>
            )}
          </span>
        );
      },
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (date) => {
        if (!date) return <Tag color="blue">No Expiry</Tag>;
        const isExpired = new Date(date) < new Date();
        return (
          <Tag color={isExpired ? 'red' : 'green'}>
            {moment(date).format('MMM DD, YYYY')}
            {isExpired && ' (Expired)'}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={status === 'active' ? 'green' : 'red'}
          icon={
            status === 'active' ? (
              <CheckCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            )
          }
        >
          {(status || 'inactive').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditCode(record)}
            className="text-blue-500"
          />
          <Popconfirm
            title="Delete this promo code?"
            onConfirm={() => handleDeleteCode(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Promo Code Management</h1>
          <p className="text-gray-500 mt-1">
            Create and manage promotional premium access codes
            <span className="ml-2 text-xs text-gray-400">
              ({ADMIN_PROMO_COLLECTION})
            </span>
          </p>
        </div>
        <Space>
          <Button
            type="primary"
            icon={<GiftOutlined />}
            onClick={handleApplyPromoCode}
            className="bg-green-500 hover:bg-green-600"
          >
            Apply to User
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateCode}
            className="bg-cyan-500 hover:bg-cyan-600"
          >
            Create Promo Code
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Active Codes"
              value={stats.activeCodes}
              prefix={<CheckCircleOutlined className="text-green-500" />}
              valueStyle={{ color: '#10b981' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Uses"
              value={stats.totalUses}
              prefix={<GiftOutlined className="text-blue-500" />}
              valueStyle={{ color: '#3b82f6' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Expired Codes"
              value={stats.expiredCodes}
              prefix={<CloseCircleOutlined className="text-red-500" />}
              valueStyle={{ color: '#ef4444' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={promoCodes}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingCode ? 'Edit Promo Code' : 'Create Promo Code'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSaveCode}
        okText="Save"
        confirmLoading={saving}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            label="Promo Code"
            name="code"
            normalize={(value) => normalizePromoCode(value)}
            rules={[
              { required: true, message: 'Please enter promo code' },
              {
                validator: (_, value) => {
                  const error = validatePromoCode(value);
                  return error ? Promise.reject(new Error(error)) : Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder="e.g., WELCOME2026"
              className="uppercase"
              maxLength={64}
              disabled={Boolean(editingCode)}
            />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Describe the offer (optional)..." />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>

          <Space align="start" className="w-full" style={{ display: 'flex' }}>
            <Form.Item
              label="Duration"
              name="durationValue"
              rules={[
                {
                  validator: (_, value) => {
                    if (durationUnit === 'lifetime') return Promise.resolve();
                    if (value == null || value < 1) {
                      return Promise.reject(new Error('Please enter duration'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              style={{ flex: 1, minWidth: 160 }}
            >
              <InputNumber
                min={1}
                max={10000}
                placeholder="30"
                className="w-full"
                disabled={durationUnit === 'lifetime'}
              />
            </Form.Item>

            <Form.Item
              label="Unit"
              name="durationUnit"
              rules={[{ required: true, message: 'Please select unit' }]}
              style={{ flex: 1, minWidth: 160 }}
            >
              <Select
                options={DURATION_UNITS}
                onChange={(unit) => {
                  if (unit === 'lifetime') {
                    form.setFieldsValue({ durationValue: 1 });
                  }
                }}
              />
            </Form.Item>
          </Space>

          <Form.Item label="Maximum Uses (Optional)" name="maxUses">
            <InputNumber
              min={1}
              max={1000000}
              placeholder="Leave empty for unlimited"
              className="w-full"
            />
          </Form.Item>

          <Form.Item label="Valid From (Optional)" name="validFrom">
            <DatePicker showTime className="w-full" />
          </Form.Item>

          <Form.Item label="Redemption Expiry Date (Optional)" name="expiryDate">
            <DatePicker className="w-full" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Apply Promo Code to User"
        open={isApplyModalVisible}
        onCancel={() => setIsApplyModalVisible(false)}
        onOk={handleApplyToUser}
        okText="Apply"
        confirmLoading={applying}
        width={500}
        destroyOnClose
      >
        <p className="text-gray-500 text-sm mb-4">
          Applies the same Premium subscription records used by the mobile
          redeem flow (subscription/current, user flags, redemption history,
          and usage counter).
        </p>
        <Form form={applyForm} layout="vertical" className="mt-4">
          <Form.Item
            label="Promo Code"
            name="promoCodeId"
            rules={[{ required: true, message: 'Please select a promo code' }]}
          >
            <Select placeholder="Select promo code" showSearch optionFilterProp="children">
              {promoCodes
                .filter((c) => {
                  if (c.status !== 'active') return false;
                  const maxUses = c.maxUses;
                  const usedCount = c.usedCount || 0;
                  if (maxUses != null && Number(maxUses) > 0 && usedCount >= Number(maxUses)) {
                    return false;
                  }
                  if (c.expiryDate && new Date(c.expiryDate) < new Date()) {
                    return false;
                  }
                  return true;
                })
                .map((code) => (
                  <Option key={code.id} value={code.id}>
                    {code.code} — {formatPromoDuration(code.duration || code)}
                    {code.description ? ` (${code.description})` : ''}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="User"
            name="username"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              placeholder="Select user"
              showSearch
              filterOption={(input, option) =>
                String(option?.children || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {users.map((user) => (
                <Option key={user.uid || user.username} value={user.username}>
                  {user.username} ({user.email}) — {user.subscriptionType}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PromoCodeManagement;
